//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { redirect, useNavigate } from 'react-router-dom';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

import { 
  CloudUpload  , // Transfer
  Streetview   , // Verify
  Dvr          , // Wf-mon
  Toc          , // Stats
  Troubleshoot , // File-Analysis
  Build        , // Admin
  Dehaze       , 
} from '@mui/icons-material';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import ShortcutIcon          from '@mui/icons-material/Shortcut'; // Shortcut

// === STORE ===
import { actions as displayActions } from 'store/modules/display';

// === HOOKS ===
import { useSession } from 'pages/PageManager/components/SessionContext';

// === API ===
import { Logo } from '../Logo/Logo';
import { ShortcutDialog } from '../ShortcutDialog/ShortcutDialog';

//#endregion

//-----------------------------------------------------------------
export function NavBar() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { user_details } = useSession();

  const theme = useTheme();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [drawerWidth, setDrawerWidth] = useState( '20rem' );
  const [searchRoles, setSearchRoles] = useState([]);

  const curr_page   = useSelector((state) => state.display.page);

  const PAGE_OCROD    = 'on-demand'  ;
  const PAGE_VERIFY   = 'verify'     ;
  const PAGE_SHORTCUT = 'shortcut'   ;
  const PAGE_WFMON    = 'workflow'   ;
  const PAGE_STATS    = 'statistics' ;
  const PAGE_ADMIN    = 'admin'      ;
  const PAGE_ANLYS    = 'analytics'  ;
  const PAGE_FILES    = 'files'  ;
  const PAGE_AUDIT_QC = 'audit-qc'   ;


  const _page_icons = {
    [ PAGE_OCROD    ]: <CloudUpload           color={PAGE_OCROD    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_VERIFY   ]: <Streetview            color={PAGE_VERIFY   === curr_page ? 'primary' : 'action'} />,
    [ PAGE_SHORTCUT ]: <ShortcutIcon          color=                                           'action'  />,
    [ PAGE_WFMON    ]: <Dvr                   color={PAGE_WFMON    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_STATS    ]: <Toc                   color={PAGE_STATS    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_ADMIN    ]: <Build                 color={PAGE_ADMIN    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_ANLYS    ]: <Troubleshoot          color={PAGE_ANLYS    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_FILES    ]: <Troubleshoot          color={PAGE_FILES    === curr_page ? 'primary' : 'action'} />,
    [ PAGE_AUDIT_QC ]: <AutoAwesomeMosaicIcon color={PAGE_AUDIT_QC === curr_page ? 'primary' : 'action'} />,

  }

  const _pages = [
    { id: 1, page: PAGE_OCROD   ,  role: 'submitter' , icon: _page_icons[ PAGE_OCROD    ] , url:  '/on-demand'  , text: 'Send Docs'        },
    { id: 2, page: PAGE_VERIFY  ,  role: 'keyer'     , icon: _page_icons[ PAGE_VERIFY   ] , url:  '/verify'     , text: 'Verify Data'      },
    { id: 8, page: PAGE_SHORTCUT,  role: 'any'       , icon: _page_icons[ PAGE_SHORTCUT ] , popup : true        , text: 'Shortcuts'        },
    { id: 3, page: PAGE_WFMON   ,  role: 'manager'   , icon: _page_icons[ PAGE_WFMON    ] , url:  '/workflow'   , text: 'Workflow Monitor' },
    { id: 4, page: PAGE_STATS   ,  role: 'reporter'  , icon: _page_icons[ PAGE_STATS    ] , url:  '/statistics' , text: 'Stats Dashboard'  },
    { id: 5, page: PAGE_ANLYS   ,  role: 'analyst'   , icon: _page_icons[ PAGE_ANLYS    ] , url:  '/analytics ' , text: 'Doc Analytics'    },
    { id: 8, page: PAGE_FILES   ,  role: 'analyst'   , icon: _page_icons[ PAGE_FILES    ] , url:  '/files'      , text: 'File Viewer'      },
    { id: 6, page: PAGE_ADMIN   ,  role: 'admin'     , icon: _page_icons[ PAGE_ADMIN    ] , url:  '/admin'      , text: 'Admin Console'    },
    { id: 7, page: PAGE_AUDIT_QC,  role: 'auditQC'   , icon: _page_icons[ PAGE_AUDIT_QC ] , url:  '/audit-qc'   , text: 'Audit QC'         },
    

  ];

  const [is_visible, setIsVisible] = useState(false);
  const [ showShortcutDialog, setShowShortcutDialog ] = useState(false);
  
  const user_roles = user_details.roles.map( x => x.RoleName );

  //#endregion

  //#region [STYLES]

  const _styles = {
    bar: {
      padding : theme.spacing( 0.5 ) ,
      height  : theme.spacing( 6.0 ) ,
      width   : '100%' ,
      height  : '5rem' ,
      display : 'flex' ,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    container: {
      whiteSpace : 'nowrap' ,
      position   : 'fixed'  ,
      overflowX  : 'hidden' ,
      zIndex     : 100      ,

      width: drawerWidth,
      [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', zIndex: 100, },

      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    list_item_icon: {
      minWidth: '3rem',
      margin: '0 0 0 9px',
    },
    logo: {
      borderRadius    : '20px',
      marginRight     : theme.spacing(1),
      padding         : '0.25rem 1rem',
      color: theme.palette.primary.main,
    }
  }

  const _attr = {
    CONTAINER: {
      sx: _styles.container,
      open: is_visible,
      onBackdropClick: () => setIsVisible(false),
      PaperProps: {
        sx: {
          // backgroundColor: theme.palette.background.primary[theme.palette.mode],
        }
      }
    }
  }

  //#endregion

  //#region [EFFECT]


  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick(event) {
    //-----------------------------------------------------------------
    setIsVisible(!is_visible);
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <>
        <IconButton name='toggle_nav_menu_btn' sx={_styles.logo} onClick={onClick}>
          <Dehaze />
        </IconButton>
        {displayNav()}
        <ShortcutDialog showPopupDialog={showShortcutDialog} setShowPopupDialog={setShowShortcutDialog}/>
      </>
      
    );
  }

  //-----------------------------------------------------------------
  function displayNav() {
    //-----------------------------------------------------------------
    return(
      <Drawer {..._attr.CONTAINER} >
        <Box sx={ { backgroundColor: theme.palette.background.primary[theme.palette.mode], height: '100%' } } >
          <Box sx={_styles.bar}>
            <IconButton name='toggle_nav_menu_btn' sx={_styles.logo} onClick={onClick}>
              <Dehaze />
            </IconButton>
            <Logo />
          </Box>
          <Box sx={ {  } } onClick={()=>setIsVisible(false)}>
            {displayPages()}
          </Box>
        </Box>
      </Drawer>
    );
  }

  //-----------------------------------------------------------------
  function displayPages() {
    //-----------------------------------------------------------------
    const pages = _pages.map( item => {
    const enabled = item.role === 'any' || user_roles.includes(item.role);
      return (
        <ListItem
          name={`menu_${item.role}_btn`}
          disableGutters
          button
          disabled={!enabled}
          key={item.page}
          sx={ [ ( enabled ? {} : { display: 'none' } ) ] }
          onClick={() => {
            if(item.popup) {
              setShowShortcutDialog(true);
            } else {
              dispatch( displayActions.setPage( item.page ) );
              navigate( item.url );
            } setIsVisible(false); } }
        >
          <ListItemIcon sx={_styles.list_item_icon} >
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItem>
      );
    } )

    return (
      <List>
        {pages}
      </List>
    )
  }

  //#endregion

  return display();
}
